/**
 * Sidebar search across every department. After a short pause (or Enter) it opens the drill-down
 * with the query pre-filled; the dialog's own search box takes over from there.
 */
import {
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'

import { ICONS } from '@/components/icons/icons'

import { useDrillDown } from '../drilldown/DrillDownContext'
import styles from './GlobalSearch.module.css'

const MIN_CHARS = 2
const DEBOUNCE_MS = 400

export function GlobalSearch({ departmentCount }: { departmentCount: number }) {
  const { openDrillDown } = useDrillDown()
  const [value, setValue] = useState('')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hintId = useId()

  const open = useCallback(
    (raw: string) => {
      const query = raw.trim()
      if (query.length < MIN_CHARS) return
      openDrillDown({ dept: null, filter: { type: 'all' }, label: 'Search results', query })
    },
    [openDrillDown],
  )

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }, [])

  useEffect(() => cancel, [cancel])

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value
      setValue(next)
      cancel()
      timer.current = setTimeout(() => open(next), DEBOUNCE_MS)
    },
    [cancel, open],
  )

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      cancel()
      open(value)
    },
    [cancel, open, value],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        cancel()
        setValue('')
      }
    },
    [cancel],
  )

  return (
    <form className={styles.wrap} role="search" onSubmit={handleSubmit}>
      <label className={styles.field}>
        <span className="visually-hidden">Search any vendor or transaction</span>
        <ICONS.search className={styles.icon} size={14} />
        <input
          className={styles.input}
          type="search"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Search any vendor or transaction…"
          autoComplete="off"
          aria-describedby={hintId}
        />
      </label>
      <div className={styles.hint} id={hintId}>
        Searches all {departmentCount} departments at once
      </div>
    </form>
  )
}
